import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import LastReviewedBanner from "@/components/shared/LastReviewedBanner";
import { FORESIGHT_SIGNALS, FORESIGHT_SCENARIOS, STANDARDS_HORIZON, PORTFOLIO_LAST_REVIEWED } from "@/data/content";
import { Radar, Compass, CalendarRange } from "lucide-react";

const TAG_COLOURS = {
  Governance: "var(--ccdr-primary)",
  Standards: "var(--ccdr-accent-dark)",
  Climate: "#0F766E",
  Sovereignty: "#7C2D12",
  Footprint: "#365314",
  Disaster: "#9F1239",
};

export default function ForesightObservatory() {
  return (
    <>
      <PageHeader
        overline="Foresight Observatory"
        title="Signals, scenarios, and the standards horizon."
        lead="A forward-looking watch on the developments that will shape resilience and the technology underpinning it. Tracked, summarised, and reviewed against credible sources so leaders can see capability before it arrives, not after."
      />

      <section className="ccdr-section">
        <div className="ccdr-container">
          <LastReviewedBanner date={PORTFOLIO_LAST_REVIEWED} source="CCDR Foresight desk" />

          {/* Signals dashboard */}
          <div className="flex items-center gap-3 mb-6">
            <Radar className="w-5 h-5" style={{ color: "var(--ccdr-primary)" }} />
            <p className="ccdr-eyebrow !mb-0">Signals dashboard</p>
          </div>
          <h2 className="ccdr-h2 mb-10">What we are watching now.</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mb-20">
            {FORESIGHT_SIGNALS.map((s) => (
              <article key={s.title} className="bg-white p-8 flex flex-col">
                <span
                  className="text-[10px] uppercase tracking-[0.22em] mb-4 self-start px-2 py-1 border"
                  style={{
                    color: TAG_COLOURS[s.tag] || "var(--ccdr-primary)",
                    borderColor: TAG_COLOURS[s.tag] || "var(--ccdr-primary)",
                  }}
                >
                  {s.tag}
                </span>
                <h3 className="ccdr-h3 mb-3">{s.title}</h3>
                <p className="text-sm text-[#4B5563] flex-1">{s.note}</p>
              </article>
            ))}
          </div>

          {/* Scenarios */}
          <div className="flex items-center gap-3 mb-6">
            <Compass className="w-5 h-5" style={{ color: "var(--ccdr-primary)" }} />
            <p className="ccdr-eyebrow !mb-0">Scenario notes</p>
          </div>
          <h2 className="ccdr-h2 mb-10">Three ways the next decade can run.</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mb-20">
            {FORESIGHT_SCENARIOS.map((s, i) => (
              <div key={s.name} className="bg-white p-8">
                <span
                  className="text-3xl tabular-nums"
                  style={{ color: "var(--ccdr-accent-dark)", fontWeight: 300 }}
                >
                  0{i + 1}
                </span>
                <h3 className="ccdr-h3 mt-4 mb-3">{s.name}</h3>
                <p className="text-[#4B5563]">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Standards horizon */}
          <div className="flex items-center gap-3 mb-6">
            <CalendarRange className="w-5 h-5" style={{ color: "var(--ccdr-primary)" }} />
            <p className="ccdr-eyebrow !mb-0">Standards horizon</p>
          </div>
          <h2 className="ccdr-h2 mb-10">When the instruments are likely to move.</h2>

          <div className="space-y-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {STANDARDS_HORIZON.map((h) => (
              <div key={h.period} className="bg-white p-8 lg:p-10 grid grid-cols-12 gap-6">
                <div className="col-span-12 lg:col-span-3">
                  <p className="ccdr-overline">{h.period}</p>
                </div>
                <div className="col-span-12 lg:col-span-9">
                  <ul className="space-y-2 text-[#4B5563]">
                    {h.items.map((it) => (
                      <li key={it} className="flex gap-3">
                        <span style={{ color: "var(--ccdr-accent-dark)" }}>—</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="ccdr-callout mt-16 max-w-3xl">
            <p className="ccdr-prose">
              <strong>Global governance watch.</strong>{" "}
              The Centre follows the UN Independent International Scientific Panel on AI and the Global Dialogue on AI
              Governance closely, alongside the OECD, IEEE, ISO/IEC, and the standards bodies whose work shapes
              resilience-critical AI.
            </p>
          </div>
        </div>
      </section>

      <CTASection
        title="Invite the Centre to brief your institution on the foresight ahead."
        primary={{ to: "/get-involved", label: "Get in touch" }}
        secondary={{ to: "/insights", label: "Or open the Open Data Sources" }}
      />
    </>
  );
}
